import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { inr } from "@/lib/format";
import { itemImageSrc } from "@/lib/itemNotes";

const ATTRIBUTE_LABEL = { veg: "Veg", non_veg: "Non-veg", egg: "Contains egg" };

/**
 * An item's details, laid out the way a guest-facing menu app shows a dish: the
 * photo on top with the name over it, the description under it, then the
 * owner's own rows (nutrition, allergens, spice level…) from the menu hub.
 *
 * Opened by a long-press on an item while ordering (touch) or by right-click →
 * "Show details" (mouse). `variant="sheet"` rises from the bottom of a phone;
 * `"modal"` is a centred card on the billing PC. Read-only: nothing here can
 * change the order, so it never stands between a captain and the punch.
 */
export default function ItemDetailsDialog({ item, onClose, variant = "modal" }) {
  const panel = useRef(null);
  const openedAt = useRef(Date.now());
  const [imageFailed, setImageFailed] = useState(false);
  const src = imageFailed ? null : itemImageSrc(item);
  const details = (item?.details || []).filter(d => d && d.label && d.value);
  const attribute = item?.attribute || (item?.veg === false ? "non_veg" : "veg");
  const isVeg = attribute === "veg";

  useEffect(() => {
    const previous = document.activeElement;
    panel.current?.focus?.();
    return () => previous?.focus?.();
  }, []);

  const onKeyDown = event => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };

  const sheet = variant === "sheet";
  return (
    <div
      className={`fixed inset-0 z-[120] flex bg-black/50 ${sheet ? "items-end" : "items-center justify-center p-4"}`}
      // Click, not mousedown, and not in the first moments: the finger that
      // long-pressed the item lifts over this backdrop, and the phone's
      // compatibility mouse events must not close the sheet it just opened.
      onClick={event => {
        if (event.target === event.currentTarget && Date.now() - openedAt.current > 400) onClose();
      }}
      onKeyDown={onKeyDown}
      data-testid="item-details-backdrop"
    >
      <section
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="item-details-name"
        className={`bg-card text-foreground shadow-xl overflow-hidden flex flex-col outline-none ${sheet ? "w-full max-h-[90vh] rounded-t-2xl animate-slide-up" : "w-full max-w-md max-h-[85vh] rounded-xl"}`}
        data-testid="item-details-dialog"
      >
        <div className={`relative shrink-0 ${src ? "aspect-[4/3]" : "min-h-[7rem]"} bg-muted`}>
          {src && (
            <img
              src={src}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setImageFailed(true)}
              data-testid="item-details-photo"
            />
          )}
          <div className={`absolute inset-x-0 bottom-0 p-4 ${src ? "bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white pt-12" : "text-foreground"}`}>
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`w-3.5 h-3.5 border-[1.5px] rounded-sm flex items-center justify-center shrink-0 bg-white ${isVeg ? "border-emerald-600" : "border-rose-600"}`}
                aria-label={ATTRIBUTE_LABEL[attribute] || attribute}
                title={ATTRIBUTE_LABEL[attribute] || attribute}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isVeg ? "bg-emerald-600" : "bg-rose-600"}`} />
              </span>
              {item?.category && <span className="text-micro font-bold uppercase opacity-90">{item.category}</span>}
            </div>
            <h2 id="item-details-name" className="text-title font-bold leading-tight" data-testid="item-details-name">{item?.name}</h2>
            {Number.isFinite(Number(item?.price)) && (
              <div className="text-body font-semibold tabular-nums mt-1">
                {inr(item.price)}{item?.variants?.length ? " onwards" : ""}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`absolute top-3 right-3 w-11 h-11 rounded-full flex items-center justify-center ${src ? "bg-black/50 text-white hover:bg-black/70" : "bg-card border hover:bg-muted"}`}
            aria-label="Close details"
            data-testid="item-details-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 flex flex-col gap-4" style={sheet ? { paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" } : undefined}>
          {item?.description ? (
            <p className="text-body text-foreground whitespace-pre-line" data-testid="item-details-description">{item.description}</p>
          ) : (
            !details.length && <p className="text-meta text-muted-foreground" data-testid="item-details-empty">No details have been added for this item yet. An admin can add them in Menu.</p>
          )}

          {item?.variants?.length > 0 && (
            <div className="flex flex-col gap-1" data-testid="item-details-variants">
              <h3 className="text-micro font-bold uppercase text-muted-foreground">Sizes</h3>
              {item.variants.map(v => (
                <div key={v.id} className="flex justify-between text-body">
                  <span>{v.name}</span>
                  <span className="tabular-nums">{inr(v.price)}</span>
                </div>
              ))}
            </div>
          )}

          {details.length > 0 && (
            <dl className="divide-y border rounded-lg" data-testid="item-details-rows">
              {details.map((d, i) => (
                <div key={`${d.label}-${i}`} className="flex justify-between gap-4 px-3 py-2.5">
                  <dt className="text-meta font-semibold text-muted-foreground shrink-0">{d.label}</dt>
                  <dd className="text-body text-right text-foreground">{d.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </section>
    </div>
  );
}
